import React, { useState, useEffect } from 'react';
import { ResourceWorkingAvailability, DayAvailability, getResourceWorkingAvailability, updateResourceWorkingAvailability, createResourceWorkingAvailability } from '../../lib/resourceAvailability';
import { Clock, Pencil, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ServiceAreasModal from './ServiceAreasModal';

interface ResourceWorkingAvailabilityProps {
  resourceId: string;
  businessId: string;
}

const DEFAULT_AVAILABILITY: Partial<ResourceWorkingAvailability> = {
  monday: { enabled: false, shifts: [] },
  tuesday: { enabled: false, shifts: [] },
  wednesday: { enabled: false, shifts: [] },
  thursday: { enabled: false, shifts: [] },
  friday: { enabled: false, shifts: [] },
  saturday: { enabled: false, shifts: [] },
  sunday: { enabled: false, shifts: [] }
};

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const ResourceWorkingAvailabilityComponent: React.FC<ResourceWorkingAvailabilityProps> = ({ resourceId, businessId }) => {
  const [availability, setAvailability] = useState<ResourceWorkingAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<ResourceWorkingAvailability | null>(null);
  const [mobileAvailable, setMobileAvailable] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);
  const [isAreasModalOpen, setIsAreasModalOpen] = useState(false);
  const [currentAreas, setCurrentAreas] = useState<string[]>([]);

  useEffect(() => {
    // Reset all state when resource changes
    console.log("Resource changed to:", resourceId);
    setMobileAvailable(false); // Reset to default false state
    setMobileLoading(true);    // Show loading state
    setAvailability(null);     // Clear availability data
    setLoading(true);          // Show loading state
    setError(null);            // Clear any errors
    setEditMode(false);        // Exit edit mode
    setFormData(null);         // Clear form data
    setCurrentAreas([]);       // Clear areas
    
    // Then fetch new data for this resource
    fetchAvailability();
    fetchMobileAvailability();
  }, [resourceId]);

  const fetchMobileAvailability = async () => {
    // Keep track of the current resourceId to prevent race conditions
    const currentResourceId = resourceId;
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      setMobileAvailable(false);
      setMobileLoading(false);
      return;
    }

    try {
      console.log(`[${currentResourceId}] Checking mobile availability...`);
      setMobileLoading(true);
      
      // Check if a record exists for this resource directly from the mobile_availability table
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('id, mobile_available')
        .eq('resource_id', currentResourceId)
        .maybeSingle();
      
      // Skip processing if resource has changed during the request
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during request, aborting`);
        return;
      }
      
      console.log(`[${currentResourceId}] Mobile availability check result:`, { 
        found: !!data, 
        mobile_available: data?.mobile_available 
      });
      
      if (error && error.code !== 'PGRST116') {
        console.error(`[${currentResourceId}] Error fetching mobile availability:`, error);
        throw error;
      }
      
      // If we found a record in mobile_availability table, set to true regardless of mobile_available field
      // This is because the existence of the record in this table means the resource should be mobile available
      if (data) {
        console.log(`[${currentResourceId}] Found mobile availability record, setting to TRUE`);
        setMobileAvailable(true);
      } else {
        console.log(`[${currentResourceId}] No mobile availability record found, setting to FALSE`);
        setMobileAvailable(false);
      }
    } catch (err) {
      // Skip error handling if resource has changed
      if (currentResourceId !== resourceId) {
        return;
      }
      
      console.error(`[${currentResourceId}] Error fetching mobile availability:`, err);
      // Set to false if there's any error
      setMobileAvailable(false);
    } finally {
      // Skip state update if resource has changed
      if (currentResourceId !== resourceId) {
        return;
      }
      
      setMobileLoading(false);
    }
  };

  const handleMobileToggle = async () => {
    if (!supabase || !availability) {
      console.error('Supabase client is not initialized or availability data is missing');
      return;
    }

    // Store the current resource ID to prevent race conditions
    const currentResourceId = resourceId;
    const newValue = !mobileAvailable;
    
    console.log(`[${currentResourceId}] Toggling mobile availability to: ${newValue}`);
    
    try {
      setMobileLoading(true);
      
      if (newValue) {
        // ENABLING MOBILE AVAILABILITY
        console.log(`[${currentResourceId}] Enabling mobile availability`);
        
        // Get resource details to get first and last name
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('first_name, last_name')
          .eq('id', currentResourceId)
          .single();

        // Abort if resource changed during the request
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }

        if (resourceError) {
          console.error(`[${currentResourceId}] Error fetching resource data:`, resourceError);
          throw resourceError;
        }

        // Create working_hours object from current availability
        const working_hours = {
          monday: {
            enabled: availability.monday.enabled,
            radius: 0,
            shifts: availability.monday.shifts,
            areas: []
          },
          tuesday: {
            enabled: availability.tuesday.enabled,
            radius: 0,
            shifts: availability.tuesday.shifts,
            areas: []
          },
          wednesday: {
            enabled: availability.wednesday.enabled,
            radius: 0,
            shifts: availability.wednesday.shifts,
            areas: []
          },
          thursday: {
            enabled: availability.thursday.enabled,
            radius: 0,
            shifts: availability.thursday.shifts,
            areas: []
          },
          friday: {
            enabled: availability.friday.enabled,
            radius: 0,
            shifts: availability.friday.shifts,
            areas: []
          },
          saturday: {
            enabled: availability.saturday.enabled,
            radius: 0,
            shifts: availability.saturday.shifts,
            areas: []
          },
          sunday: {
            enabled: availability.sunday.enabled,
            radius: 0,
            shifts: availability.sunday.shifts,
            areas: []
          }
        };

        // First try to delete any existing record to avoid conflicts
        await supabase
          .from('mobile_availability')
          .delete()
          .eq('resource_id', currentResourceId);
        
        // Abort if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }
          
        console.log(`[${currentResourceId}] Deleted any existing mobile_availability records`);

        // Create new record to ensure consistency
        console.log(`[${currentResourceId}] Creating fresh mobile_availability record`);
        const { error: insertError } = await supabase
          .from('mobile_availability')
          .insert({
            resource_id: currentResourceId,
            business_id: businessId,
            mobile_available: true, // Always set to true when explicitly enabled
            working_hours,
            first_name: resourceData.first_name,
            last_name: resourceData.last_name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        // Abort if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }

        if (insertError) {
          console.error(`[${currentResourceId}] Error creating mobile availability record:`, insertError);
          throw insertError;
        }
        
        console.log(`[${currentResourceId}] Successfully enabled mobile availability`);
      } else {
        // DISABLING MOBILE AVAILABILITY
        console.log(`[${currentResourceId}] Disabling mobile availability`);
        
        // When disabling, just delete the record completely
        const { error: deleteError } = await supabase
          .from('mobile_availability')
          .delete()
          .eq('resource_id', currentResourceId);

        // Abort if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }

        if (deleteError) {
          console.error(`[${currentResourceId}] Error deleting mobile availability record:`, deleteError);
          throw deleteError;
        }
        
        console.log(`[${currentResourceId}] Successfully disabled mobile availability by deleting record`);
      }

      // Skip remaining operations if resource has changed
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during operation, skipping final updates`);
        return;
      }

      // Update UI state
      setMobileAvailable(newValue);
      
      // Force refresh data from the database
      await fetchMobileAvailability();
      
    } catch (err) {
      // Skip error handling if resource has changed
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during operation, skipping error handling`);
        return;
      }
      
      console.error(`[${currentResourceId}] Error updating mobile availability:`, err);
      // Revert the UI state if there was an error
      setMobileAvailable(!newValue);
      // Force refresh data from the database
      await fetchMobileAvailability();
    } finally {
      // Skip final cleanup if resource has changed
      if (currentResourceId !== resourceId) {
        return;
      }
      
      setMobileLoading(false);
    }
  };

  const fetchAvailability = async () => {
    // Track current resource to prevent race conditions
    const currentResourceId = resourceId;
    
    try {
      console.log(`[${currentResourceId}] Fetching availability for resource`);
      setLoading(true);
      setError(null);
      
      // Clear existing data first to prevent displaying stale data
      setAvailability(null);
      setFormData(null);
      
      let data = await getResourceWorkingAvailability(currentResourceId);
      
      // Abandon operation if resource changed
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during availability fetch, aborting`);
        return;
      }
      
      console.log(`[${currentResourceId}] Availability data fetched:`, data ? 'Data found' : 'No data found');
      
      if (!data) {
        console.log(`[${currentResourceId}] No availability data found, creating default`);
        await createResourceWorkingAvailability(currentResourceId, businessId, DEFAULT_AVAILABILITY);
        data = await getResourceWorkingAvailability(currentResourceId);
        
        // Abandon operation if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during availability creation, aborting`);
          return;
        }
        
        if (!data) {
          throw new Error('Failed to initialize availability data');
        }
      }
      
      // Log resource availability to debug
      console.log(`[${currentResourceId}] Setting availability data with days:`, 
        Object.keys(data).map(day => {
          const dayKey = day as keyof ResourceWorkingAvailability;
          const dayData = data[dayKey] as DayAvailability;
          return `${day}: ${dayData.enabled ? 'enabled' : 'disabled'}`;
        }).join(', ')
      );
      
      // Only update state if we're still looking at the same resource
      if (currentResourceId === resourceId) {
        setAvailability(data);
        setFormData(data);
      } else {
        console.log(`[${currentResourceId}] Resource changed, not updating state`);
      }
    } catch (err) {
      // Only update error state if we're still on the same resource
      if (currentResourceId === resourceId) {
        console.error(`[${currentResourceId}] Error fetching availability:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load availability data');
      }
    } finally {
      // Only update loading state if we're still on the same resource
      if (currentResourceId === resourceId) {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!formData) return;
    
    try {
      setLoading(true);
      
      // First update the resource's working availability
      await updateResourceWorkingAvailability(resourceId, formData);
      console.log('Updated resource working availability');
      
      // Check if this resource has mobile availability enabled
      if (mobileAvailable && supabase) {
        console.log('Resource has mobile availability, syncing working hours');
        
        // Check if a record exists in mobile_availability
        const { data: existingRecord, error: checkError } = await supabase
          .from('mobile_availability')
          .select('id, mobile_available, first_name, last_name, working_hours')
          .eq('resource_id', resourceId)
          .maybeSingle();
          
        // Convert formData to mobile availability working hours format
        const working_hours = {
          monday: {
            enabled: formData.monday.enabled,
            shifts: formData.monday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.monday?.areas || []
          },
          tuesday: {
            enabled: formData.tuesday.enabled,
            shifts: formData.tuesday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.tuesday?.areas || []
          },
          wednesday: {
            enabled: formData.wednesday.enabled,
            shifts: formData.wednesday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.wednesday?.areas || []
          },
          thursday: {
            enabled: formData.thursday.enabled,
            shifts: formData.thursday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.thursday?.areas || []
          },
          friday: {
            enabled: formData.friday.enabled,
            shifts: formData.friday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.friday?.areas || []
          },
          saturday: {
            enabled: formData.saturday.enabled,
            shifts: formData.saturday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.saturday?.areas || []
          },
          sunday: {
            enabled: formData.sunday.enabled,
            shifts: formData.sunday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.sunday?.areas || []
          }
        };
        
        if (existingRecord) {
          // Update existing record
          console.log('Updating existing mobile_availability record');
          const { error: updateError } = await supabase
            .from('mobile_availability')
            .update({ 
              working_hours,
              mobile_available: true, // Always set to true when a record exists
              updated_at: new Date().toISOString()
            })
            .eq('resource_id', resourceId);
  
          if (updateError) {
            console.error('Error updating mobile availability:', updateError);
            throw updateError;
          }
        } else {
          // Need to create a new record - get resource details first
          console.log('No mobile_availability record exists, creating new one');
          const { data: resourceData, error: resourceError } = await supabase
            .from('resources')
            .select('first_name, last_name')
            .eq('id', resourceId)
            .single();
  
          if (resourceError) {
            console.error('Error fetching resource data:', resourceError);
            throw resourceError;
          }
          
          // Create new mobile_availability record
          const { error: insertError } = await supabase
            .from('mobile_availability')
            .insert({
              resource_id: resourceId,
              business_id: businessId,
              mobile_available: true,
              working_hours,
              first_name: resourceData.first_name,
              last_name: resourceData.last_name,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
  
          if (insertError) {
            console.error('Error creating mobile availability record:', insertError);
            throw insertError;
          }
        }
        
        console.log('Successfully synchronized mobile availability working hours');
      }

      // Update local state
      setAvailability(formData);
      setEditMode(false);
      
      // Refresh mobile availability status
      await fetchMobileAvailability();
      
    } catch (err) {
      console.error('Error saving availability:', err);
      setError(err instanceof Error ? err.message : 'Failed to save availability data');
      
      // Refresh mobile availability status
      await fetchMobileAvailability();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (dayKey: keyof ResourceWorkingAvailability) => {
    if (!formData) return;

    const dayAvailability = formData[dayKey] as DayAvailability;
    setFormData({
      ...formData,
      [dayKey]: {
        enabled: !dayAvailability.enabled,
        shifts: dayAvailability.enabled ? [] : [{ start: '09:00', end: '17:00' }]
      }
    });
  };

  const handleShiftChange = (dayKey: keyof ResourceWorkingAvailability, start: string, end: string) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [dayKey]: {
        enabled: true,
        shifts: [{ start, end }]
      }
    });
  };

  const fetchCurrentAreas = async (day: string) => {
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('working_hours')
        .eq('resource_id', resourceId)
        .single();

      if (error) {
        console.error('Error fetching current areas:', error);
        return;
      }

      const dayKey = day.toLowerCase();
      const areas = data?.working_hours?.[dayKey]?.areas || [];
      setCurrentAreas(areas);
    } catch (err) {
      console.error('Error fetching current areas:', err);
    }
  };

  const handleAreasClick = async (day: string) => {
    setSelectedDay(day);
    await fetchCurrentAreas(day);
    setIsAreasModalOpen(true);
  };

  const handleSaveAreas = async (selectedAreas: string[]) => {
    if (!supabase || !selectedDay) return;

    try {
      console.log('Saving areas:', { selectedAreas, selectedDay });
      setMobileLoading(true);

      // First get current mobile availability data
      const { data: currentData, error: fetchError } = await supabase
        .from('mobile_availability')
        .select('working_hours, postal_codes')
        .eq('resource_id', resourceId)
        .single();

      // Get the selected service areas' postal codes
      const { data: serviceAreas, error: serviceAreasError } = await supabase
        .from('service_areas')
        .select('*')
        .in('id', selectedAreas);

      if (serviceAreasError) {
        console.error('Error fetching service areas:', serviceAreasError);
        throw serviceAreasError;
      }

      // Combine all postal codes from selected service areas
      const allPostalCodes = serviceAreas?.reduce((acc: string[], area) => {
        return [...acc, ...(area.postal_codes || [])];
      }, []) || [];

      console.log('Service areas data:', serviceAreas);
      console.log('Combined postal codes:', allPostalCodes);

      // Initialize or get existing working_hours
      const working_hours = currentData?.working_hours || {
        monday: { enabled: false, shifts: [], areas: [] },
        tuesday: { enabled: false, shifts: [], areas: [] },
        wednesday: { enabled: false, shifts: [], areas: [] },
        thursday: { enabled: false, shifts: [], areas: [] },
        friday: { enabled: false, shifts: [], areas: [] },
        saturday: { enabled: false, shifts: [], areas: [] },
        sunday: { enabled: false, shifts: [], areas: [] }
      };

      // Update working_hours for the selected day
      const dayKey = selectedDay.toLowerCase() as keyof ResourceWorkingAvailability;
      working_hours[dayKey] = {
        ...working_hours[dayKey],
        areas: selectedAreas,
        enabled: true,
        shifts: working_hours[dayKey]?.shifts || [{ start: '09:00', end: '17:00' }]
      };

      // Initialize or get existing postal_codes
      const postal_codes = {
        ...(currentData?.postal_codes || {}),
        [dayKey]: allPostalCodes
      };

      console.log('Updating with:', {
        working_hours,
        postal_codes,
        dayKey,
        selectedAreas
      });

      // First delete any existing record
      await supabase
        .from('mobile_availability')
        .delete()
        .eq('resource_id', resourceId);

      // Then insert the new record
      const { error: insertError } = await supabase
        .from('mobile_availability')
        .insert({
          resource_id: resourceId,
          business_id: businessId,
          mobile_available: true,
          working_hours,
          postal_codes,
          updated_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('Error inserting mobile availability:', insertError);
        throw insertError;
      }

      // Update the local state with the new working hours
      if (availability) {
        const dayKey = selectedDay.toLowerCase() as keyof ResourceWorkingAvailability;
        const currentDay = availability[dayKey] as DayAvailability;
        const updatedAvailability = {
          ...availability,
          [dayKey]: {
            enabled: currentDay.enabled,
            shifts: currentDay.shifts,
            areas: selectedAreas
          }
        };
        setAvailability(updatedAvailability);
      }

      // Update current areas state
      setCurrentAreas(selectedAreas);

      console.log('Successfully updated mobile availability');
      setIsAreasModalOpen(false);
    } catch (err) {
      console.error('Error updating service areas:', err);
    } finally {
      setMobileLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading availability...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (!availability) {
    return <div className="p-4 text-center">No availability data found</div>;
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
        <h3 className="text-sm font-medium text-gray-900">Working Hours</h3>
        <div className="flex items-center space-x-4">
          {editMode ? (
            <div className="flex items-center space-x-2">
              <button
                onClick={() => {
                  setFormData(availability);
                  setEditMode(false);
                }}
                className="text-xs text-gray-500 hover:text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={loading}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                {loading ? 'Saving...' : 'Save'}
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditMode(true)}
              className="text-gray-400 hover:text-gray-600"
            >
              <Pencil className="h-4 w-4" />
            </button>
          )}
        </div>
      </div>

      <div className="p-4 border-b border-gray-200 bg-gray-50">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-gray-400" />
            <span className="text-sm text-gray-600">Mobile Service</span>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleMobileToggle}
              disabled={mobileLoading}
              className={`
                relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                ${mobileAvailable ? 'bg-blue-600' : 'bg-gray-200'}
              `}
            >
              <span
                className={`
                  pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                  transition duration-200 ease-in-out
                  ${mobileAvailable ? 'translate-x-5' : 'translate-x-0'}
                `}
              />
            </button>
            <span className="text-sm text-gray-600">
              {mobileLoading ? 'Loading...' : (mobileAvailable ? 'Enabled' : 'Disabled')}
            </span>
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="space-y-4">
          {DAYS.map(({ key, label }) => {
            const dayAvailability = (editMode ? formData : availability)?.[key as keyof ResourceWorkingAvailability] as DayAvailability;
            
            return (
              <div key={key} className="flex items-center justify-between">
                <div className="w-24 text-sm text-gray-600 capitalize">{label}</div>
                {editMode ? (
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={dayAvailability.enabled}
                        onChange={() => handleToggleDay(key as keyof ResourceWorkingAvailability)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-600">Available</span>
                    </label>
                    {dayAvailability.enabled && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={dayAvailability.shifts[0]?.start || '09:00'}
                          onChange={(e) => handleShiftChange(key as keyof ResourceWorkingAvailability, e.target.value, dayAvailability.shifts[0]?.end || '17:00')}
                          className="w-24 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                        <span className="text-sm text-gray-500">to</span>
                        <input
                          type="time"
                          value={dayAvailability.shifts[0]?.end || '17:00'}
                          onChange={(e) => handleShiftChange(key as keyof ResourceWorkingAvailability, dayAvailability.shifts[0]?.start || '09:00', e.target.value)}
                          className="w-24 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-900 flex items-center space-x-4">
                    {dayAvailability.enabled ? (
                      <>
                        <div className="flex items-center space-x-2">
                          <span className="text-green-600">Available</span>
                          <span className="text-gray-500">
                            {dayAvailability.shifts[0]?.start} - {dayAvailability.shifts[0]?.end}
                          </span>
                        </div>
                        {mobileAvailable && (
                          <button
                            onClick={() => handleAreasClick(label)}
                            className="px-2 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 border border-blue-200 rounded-md hover:border-blue-300 transition-colors duration-200 flex items-center"
                          >
                            <span>Areas</span>
                            {dayAvailability?.areas && dayAvailability.areas.length > 0 && (
                              <span className="ml-1.5 inline-flex items-center justify-center bg-blue-100 text-blue-800 text-xs font-semibold px-1.5 py-0.5 rounded-full">
                                {dayAvailability.areas.length}
                              </span>
                            )}
                          </button>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">Not available</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isAreasModalOpen && selectedDay && (
        <ServiceAreasModal
          isOpen={isAreasModalOpen}
          onClose={() => setIsAreasModalOpen(false)}
          businessId={businessId}
          day={selectedDay}
          onSave={handleSaveAreas}
          currentAreas={currentAreas}
        />
      )}
    </div>
  );
};

// Wrap with a keyed component to force remounting when resourceId changes
const KeyedResourceWorkingAvailability: React.FC<ResourceWorkingAvailabilityProps> = (props) => {
  // Use resourceId as a key to ensure complete component remount when resource changes
  return <ResourceWorkingAvailabilityComponent key={props.resourceId} {...props} />;
};

export default KeyedResourceWorkingAvailability; 